import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('mobile_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const UNSUPPORTED_PLATFORMS = [];

function OrderInvoiceCard({ order, item, onDone, couriers }) {
  const [courier, setCourier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [step, setStep] = useState('idle');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');

  const handleRegister = async () => {
    if (!courier || !trackingNumber.trim()) {
      setMessage('택배사와 운송장번호를 입력해주세요');
      setMessageType('error');
      return;
    }
    setStep('saving');
    setMessage('');
    try {
      await api.patch(`/purchases/${item.id}/tracking`, { courier, tracking_number: trackingNumber.trim() });
    } catch (err) {
      setStep('idle');
      setMessage('송장 등록에 실패했습니다: ' + (err.response?.data?.message || err.message));
      setMessageType('error');
      return;
    }

    setStep('sending');
    try {
      await api.post('/orders/invoice-send-single', { order_item_id: item.id, platform: order.platform });
      setStep('done');
      setMessage('송장이 등록되고 판매처에 전송됐습니다');
      setMessageType('success');
      setTimeout(() => onDone(item.id), 1200);
    } catch (err) {
      setStep('sendFailed');
      setMessage('운송장은 저장됐지만 판매처 전송에 실패했습니다: ' + (err.response?.data?.message || err.message));
      setMessageType('error');
    }
  };

  const handleRetrySend = async () => {
    setStep('sending');
    setMessage('');
    try {
      await api.post('/orders/invoice-send-single', { order_item_id: item.id, platform: order.platform });
      setStep('done');
      setMessage('판매처 전송이 완료됐습니다');
      setMessageType('success');
      setTimeout(() => onDone(item.id), 1200);
    } catch (err) {
      setStep('sendFailed');
      setMessage('재전송 실패: ' + (err.response?.data?.message || err.message));
      setMessageType('error');
    }
  };

  const pr = item.purchase_record || {};
  const isUnsupported = UNSUPPORTED_PLATFORMS.includes(order.platform);
  const busy = step === 'saving' || step === 'sending';

  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: '12px', padding: '14px', marginBottom: '12px', background: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '8px', background: '#E6F1FB', color: '#185FA5', fontWeight: '500' }}>{order.platform}</span>
        <span style={{ fontSize: '11px', color: '#aaa' }}>{order.ordered_at ? new Date(order.ordered_at).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' }) : '-'} 주문</span>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
        <div style={{ width: '52px', height: '52px', borderRadius: '8px', overflow: 'hidden', background: '#f0f0f0', flexShrink: 0 }}>
          {item.product_thumbnail && (
            <img src={`https://admin.jinhalife.co.kr${item.product_thumbnail}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '2px' }}>{item.product_name}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>수량 {item.quantity}개 · {(item.sale_price || 0).toLocaleString()}원</div>
        </div>
      </div>

      <div style={{ background: '#f7f7f7', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
        <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>수령인 정보</div>
        <div style={{ fontSize: '13px' }}>{order.receiver_name} · {order.receiver_phone}</div>
        <div style={{ fontSize: '12px', color: '#666', margin: '4px 0' }}>{order.address1} {order.address2}</div>
        <div style={{ fontSize: '12px', color: '#aaa' }}>구매자 {order.buyer_name}</div>
      </div>

      <div style={{ background: '#f7f7f7', borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
        <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>구매처 정보</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '2px 0' }}>
          <span style={{ color: '#888' }}>구매처</span><span>{pr.purchase_platform || '-'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '2px 0' }}>
          <span style={{ color: '#888' }}>구매아이디</span><span>{pr.purchase_account || '-'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '2px 0' }}>
          <span style={{ color: '#888' }}>구매가</span><span>{pr.purchase_price ? pr.purchase_price.toLocaleString() + '원' : '-'}</span>
        </div>
        {pr.purchase_platform && (() => {
          const platform = pr.purchase_platform;
          const orderNo = pr.purchase_order_number;
          const cartNo = pr.cart_number;
          let url = null;
          if (platform === '11번가') url = 'https://buy.11st.co.kr/my11st/order/OrderList.tmall';
          else if (platform === '지마켓' && cartNo) url = `https://my.gmarket.co.kr/ko/pc/detail/basic/${cartNo}`;
          else if (platform === '옥션' && orderNo) url = `https://escrow.auction.co.kr/Close/OrderProcessDetailLayer.aspx?order_no=${orderNo}`;
          else if (platform === '네이버' && orderNo) url = `https://orders.pay.naver.com/order/status/${orderNo}`;
          else if (platform === 'CJ더마켓' && orderNo) url = `https://www.cjthemarket.com/pc/myOrder/orderDetail?odrNo=${orderNo}`;
          else if (platform === '컬리' && orderNo) url = `https://www.kurly.com/mypage/order/${orderNo}`;
          else if (platform === '롯데온' && orderNo) url = `https://www.lotteon.com/p/order/claim/orderDetail?odNo=${orderNo}`;
          else if (platform === 'SSG' && orderNo) url = `https://pay.ssg.com/myssg/orderInfoDetail.ssg?orordNo=${orderNo}&viewType=Ssg`;
          else if (platform === '풀무원' && cartNo) url = `https://shop.pulmuone.co.kr/mypage/orderDetail?order=${cartNo}&before=orderHistory`;
          return (
            <button
              onClick={() => { if (url) window.open(url, '_blank'); else alert('미구현입니다. 수동으로 확인해주세요.'); }}
              style={{ display: 'block', width: '100%', textAlign: 'center', marginTop: '8px', fontSize: '12px', padding: '8px', borderRadius: '8px', border: '1px solid #378ADD', background: '#fff', color: '#378ADD' }}>
              구매링크이동
            </button>
          );
        })()}
      </div>

      {isUnsupported ? (
        <div style={{ background: '#FAEEDA', borderRadius: '8px', padding: '12px', fontSize: '12px', color: '#854F0B', textAlign: 'center' }}>
          이 판매처는 자동 전송이 지원되지 않습니다. PC에서 직접 송장을 등록해주세요.
        </div>
      ) : step !== 'done' && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
            <select value={courier} onChange={e => setCourier(e.target.value)} disabled={busy}
              style={{ width: '100%', fontSize: '13px', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', boxSizing: 'border-box' }}>
              <option value="">택배사 선택</option>
              {couriers.map(c => <option key={c.id} value={c.code}>{c.name}</option>)}
            </select>
            <input value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} placeholder="운송장번호" disabled={busy}
              style={{ width: '100%', fontSize: '13px', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
          </div>

          {step === 'sendFailed' ? (
            <button onClick={handleRetrySend} disabled={busy}
              style={{ width: '100%', fontSize: '14px', padding: '12px', borderRadius: '8px', border: 'none', background: '#E24B4A', color: '#fff', fontWeight: '500' }}>
              판매처 재전송
            </button>
          ) : (
            <button onClick={handleRegister} disabled={busy}
              style={{ width: '100%', fontSize: '14px', padding: '12px', borderRadius: '8px', border: 'none', background: busy ? '#ccc' : '#1D9E75', color: '#fff', fontWeight: '500' }}>
              {step === 'saving' ? '송장 저장 중...' : step === 'sending' ? '판매처 전송 중...' : '송장등록'}
            </button>
          )}
        </>
      )}

      {message && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: messageType === 'success' ? '#1D9E75' : '#E24B4A' }}>{message}</div>
      )}
    </div>
  );
}

export default function MobileInvoice() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [couriers, setCouriers] = useState([]);

  useEffect(() => {
    api.get('/couriers').then(r => setCouriers(r.data || [])).catch(() => {});
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/orders', { params: { status: 'buy', page, limit: 10 } });
      setOrders(r.data.orders || []);
      setTotalPages(r.data.totalPages || 1);
      setTotal(r.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleItemDone = (itemId) => {
    fetchOrders();
  };

  const cards = [];
  orders.forEach(order => {
    (order.items || []).forEach(item => {
      cards.push({ order, item });
    });
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: '16px', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <button onClick={() => navigate('/m')} style={{ border: 'none', background: 'none', fontSize: '20px', padding: 0 }}>←</button>
        <span style={{ fontSize: '17px', fontWeight: '500' }}>송장 등록</span>
      </div>
      <div style={{ fontSize: '12px', color: '#999', marginBottom: '16px' }}>구매완료 {total}건</div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#aaa', fontSize: '13px' }}>불러오는 중...</div>
      ) : cards.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#aaa', fontSize: '13px' }}>구매완료된 주문이 없습니다</div>
      ) : (
        cards.map(({ order, item }) => (
          <OrderInvoiceCard key={item.id} order={order} item={item} onDone={handleItemDone} couriers={couriers} />
        ))
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ fontSize: '13px', padding: '8px 14px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff' }}>이전</button>
          <span style={{ fontSize: '13px', color: '#666' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ fontSize: '13px', padding: '8px 14px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff' }}>다음</button>
        </div>
      )}
    </div>
  );
}
